import { Languages } from "@/constants/enums";

const ARABIC_LOCALE = "ar-EG";
const LATIN_NUMERALS = { numberingSystem: "latn" as const };

const ARABIC_NUMBER_FORMATTER = new Intl.NumberFormat(
  ARABIC_LOCALE,
  LATIN_NUMERALS,
);

const ARABIC_DATE_FORMATTER = new Intl.DateTimeFormat(ARABIC_LOCALE, {
  ...LATIN_NUMERALS,
  year: "numeric",
  month: "numeric",
  day: "numeric",
});

const ARABIC_DATE_LONG_FORMATTER = new Intl.DateTimeFormat(ARABIC_LOCALE, {
  ...LATIN_NUMERALS,
  year: "numeric",
  month: "long",
  day: "numeric",
});

const ARABIC_DATE_TIME_FORMATTER = new Intl.DateTimeFormat(ARABIC_LOCALE, {
  ...LATIN_NUMERALS,
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
});

const ARABIC_LONG_DATE_FORMATTER = new Intl.DateTimeFormat(ARABIC_LOCALE, {
  ...LATIN_NUMERALS,
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
});

const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat(ARABIC_LOCALE, {
  numeric: "auto",
  ...LATIN_NUMERALS,
});

/** Arabic text with Western (0-9) numerals. */
export function formatArabicNumber(number: number) {
  return ARABIC_NUMBER_FORMATTER.format(number);
}

/** Arabic short date with Western numerals. */
export function formatArabicDate(date: Date) {
  return ARABIC_DATE_FORMATTER.format(date);
}

/** Arabic long month date with Western numerals. */
export function formatArabicDateLong(date: Date) {
  return ARABIC_DATE_LONG_FORMATTER.format(date);
}

/** Arabic date-time with Western numerals. */
export function formatArabicDateTime(date: Date) {
  return ARABIC_DATE_TIME_FORMATTER.format(date);
}

/** Arabic weekday + long date with Western numerals. */
export function formatArabicLongDate(date: Date) {
  return ARABIC_LONG_DATE_FORMATTER.format(date);
}

export function formatArabicRelativeTime(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
) {
  return RELATIVE_TIME_FORMATTER.format(value, unit);
}

export function formatArabicRelativeTimeFromNow(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = then - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return formatArabicRelativeTime(Math.round(diffSec), "second");
  if (abs < 3600)
    return formatArabicRelativeTime(Math.round(diffSec / 60), "minute");
  if (abs < 86400)
    return formatArabicRelativeTime(Math.round(diffSec / 3600), "hour");
  return formatArabicRelativeTime(Math.round(diffSec / 86400), "day");
}

export function formatOptionalArabicDate(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return formatArabicDate(date);
}

export function formatOptionalArabicDateLong(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return formatArabicDateLong(date);
}

export function formatOptionalArabicDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return formatArabicDateTime(date);
}

export function formatCurrency(amount: number, currency: string = "USD") {
  const formattedAmount = formatArabicNumber(amount);
  const suffix = currency === "EGP" ? "ج.م" : currency;

  return `${formattedAmount} ${suffix}`;
}
export function formatPercentage(number: number) {
  return `${number.toFixed(2)}%`;
}

/** Human-readable file size (e.g. 1536 → "1.5 ك.ب"). Returns "—" for null/undefined. */
export function formatBytes(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return "—";
  if (bytes === 0) return "0 بايت";
  const units = ["بايت", "ك.ب", "م.ب", "ج.ب", "ت.ب"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  const formatted = exponent === 0 ? String(value) : value.toFixed(1);
  return `${formatArabicNumber(Number(formatted))} ${units[exponent]}`;
}

export function formatNumber(number: number) {
  return formatArabicNumber(number);
}

export function formatDuration(minutes: number, locale: Languages): string {
  const seconds = minutes * 60;
  const hourText = locale === Languages.ENGLISH ? "h" : " ساعة";
  const minuteText = locale === Languages.ENGLISH ? "m" : " دقيقة";
  const secondText = locale === Languages.ENGLISH ? "s" : " ثانية";

  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    return `${hours}${hourText}`;
  } else if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}${minuteText}`;
  } else {
    return `${Math.floor(seconds)}${secondText}`;
  }
}

export function formatDateTime(date: Date) {
  return formatArabicDateTime(date);
}

export function formatOptionalDateTime(iso?: string | null): string {
  return formatOptionalArabicDateTime(iso);
}

export function formatLargeNumber(value: number): string {
  if (value >= 1000) {
    const formattedValue = (value / 1000).toFixed(1);
    return `${formattedValue}k+`;
  }
  return value.toString();
}

export function formatPaymentStatus(status: string): string {
  switch (status?.toUpperCase()) {
    case "PAID":
    case "COMPLETED":
    case "SUCCEEDED":
      return "مدفوعة";
    case "FAILED":
      return "غير مدفوعة";
    case "PENDING":
    case "PROCESSING":
      return "قيد الانتظار";
    case "REFUNDED":
      return "مستردة";
    case "CANCELLED":
      return "ملغاة";
    default:
      return status;
  }
}
export function formatPaymentMethod(method: string | undefined): string {
  if (!method) return "غير محدد";

  switch (method?.toUpperCase()) {
    case "STRIPE":
    case "CARD":
    case "CREDIT_CARD":
    case "VISA":
    case "MASTERCARD":
      return "بطاقة ائتمان";
    case "PHONE_CASH":
    case "WALLET":
    case "VODAFONE_CASH":
      return "محفظة إلكترونية";
    case "CASH":
      return "نقدًا";
    default:
      return method;
  }
}
